import * as React from 'react';

import { cn } from '@/lib/utils';
import { Stagger, RiseIn } from '@/components/ui/motion';
import { Aide } from '@/components/ui/aide';

/**
 * Layout grammars, one per functional archetype.
 *
 * Forty-six screens do not need forty-six identities — they belong to six
 * families, and the family is what should be recognisable. Someone should
 * know whether they are on a pilotage, a registry, a record, a form, a map or
 * a console *before reading the title*, from the shape of the page alone.
 *
 * Identity here comes from structure and density, never from colour: giving
 * each family its own hue would rebuild the rainbow the token system exists
 * to prevent. The palette stays shared; the skeleton differs.
 *
 *   Pilotage   instrument band on a dark surface, then decisions, then charts
 *   Registry  metric strip, sticky filter bar, then the records
 *   Record    identity band, then a split of state (left) and actions (right)
 *   Form      one narrow column, numbered fieldsets, sticky commit bar
 *   Map       full-bleed canvas with an overlaid, synchronised list
 *   Console   monospaced instrument surface for logs and system state
 */

/* ------------------------------------------------------------------ */
/* Colonnes fixes (maître-détail)                                       */
/* ------------------------------------------------------------------ */

/**
 * Une colonne qui se colle sous la barre du haut, puis défile à l'intérieur
 * d'elle-même — la grammaire commune de tous les écrans à deux colonnes
 * (factures, intégrité des missions, détail d'un convoi, fiches camion et
 * location, répartition).
 *
 * Le geste voulu: l'en-tête de page défile avec la page; dès que le haut de la
 * colonne atteint le haut de la zone visible (sous la barre, 4 rem, plus 1 rem
 * d'air), elle reste en place et c'est SON contenu qui défile. Dessous, tout
 * s'empile et la page défile normalement: aucun défilement imbriqué.
 *
 *  - Hauteur: l'écran moins 5 rem en haut (barre + air) et 5 rem en bas pour le
 *    pied de page et la marge de la page. Sans cette seconde réserve, en bout
 *    de page le pied remontait la colonne sous la barre du haut et cachait le
 *    début de son contenu.
 *  - Le défilement interne ne s'ouvre qu'une fois la colonne collée
 *    (`data-collee`, posé par `useColonneCollee`): tant qu'elle n'a pas
 *    atteint le haut, la molette fait défiler la page — sans quoi une colonne
 *    dont le bas dépasse de l'écran avalerait la molette et laisserait la
 *    dernière carte coupée. Sans le script, le défilement interne reste
 *    ouvert: rien n'est jamais inaccessible.
 *  - `pb-10`: de l'air sous le dernier élément, pour qu'il ne soit jamais
 *    rogné contre le bord de la colonne.
 *
 * Les classes sont écrites en entier (Tailwind ne devine pas `${point}:`).
 */
const COLONNE_FIXE = {
  lg: 'lg:sticky lg:top-20 lg:max-h-[calc(100vh-10rem)] lg:self-start lg:overflow-y-auto lg:overscroll-contain lg:pb-10 lg:pr-1 lg:data-[collee=false]:overflow-y-hidden',
  xl: 'xl:sticky xl:top-20 xl:max-h-[calc(100vh-10rem)] xl:self-start xl:overflow-y-auto xl:overscroll-contain xl:pb-10 xl:pr-1 xl:data-[collee=false]:overflow-y-hidden',
};
const LARGEUR_POINT = { lg: 1024, xl: 1280 };
const HAUT_COLLE_PX = 80; // top-20
const TOLERANCE_COLLE_PX = 8;

const aucunEcran = () => typeof window === 'undefined' || typeof window.matchMedia !== 'function';

/** Pose `data-collee` sur la colonne selon qu'elle a atteint le haut. */
function useColonneCollee(ref, point) {
  React.useEffect(() => {
    const element = ref.current;
    if (!element || aucunEcran()) return undefined;
    const requete = window.matchMedia(`(min-width: ${LARGEUR_POINT[point] ?? LARGEUR_POINT.lg}px)`);
    let image = 0;
    const calculer = () => {
      image = 0;
      if (!requete.matches) {
        element.removeAttribute('data-collee');
        return;
      }
      const racine = document.documentElement;
      // En bout de page la colonne ne peut plus monter: elle est « collée » de
      // fait, même si le pied de page l'a laissée un peu plus bas que 5 rem.
      const finDePage = window.scrollY + window.innerHeight >= racine.scrollHeight - 2;
      const collee =
        finDePage || element.getBoundingClientRect().top <= HAUT_COLLE_PX + TOLERANCE_COLLE_PX;
      element.setAttribute('data-collee', collee ? 'true' : 'false');
    };
    const planifier = () => {
      if (!image) image = window.requestAnimationFrame(calculer);
    };
    calculer();
    window.addEventListener('scroll', planifier, { passive: true });
    window.addEventListener('resize', planifier);
    return () => {
      if (image) window.cancelAnimationFrame(image);
      window.removeEventListener('scroll', planifier);
      window.removeEventListener('resize', planifier);
      element.removeAttribute('data-collee');
    };
  }, [ref, point]);
}

/** La grille à deux colonnes. `colonnes` porte le gabarit, préfixé de son point
 *  de rupture (ex. `lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]`). */
const SplitFixe = ({ colonnes, className, children, ...rest }) => (
  <div className={cn('grid gap-4', colonnes, className)} {...rest}>
    {children}
  </div>
);
SplitFixe.displayName = 'SplitFixe';

/** Une colonne de `SplitFixe`. `point` doit être celui du gabarit de la grille. */
const ColonneFixe = React.forwardRef(
  ({ as: Balise = 'div', point = 'lg', className, children, ...rest }, ref) => {
    const interne = React.useRef(null);
    const poser = React.useCallback(
      (noeud) => {
        interne.current = noeud;
        if (typeof ref === 'function') ref(noeud);
        else if (ref) ref.current = noeud;
      },
      [ref],
    );
    useColonneCollee(interne, point);
    return (
      <Balise ref={poser} className={cn(COLONNE_FIXE[point] ?? COLONNE_FIXE.lg, className)} {...rest}>
        {children}
      </Balise>
    );
  },
);
ColonneFixe.displayName = 'ColonneFixe';

/* ------------------------------------------------------------------ */
/* Pilotage                                                             */
/* ------------------------------------------------------------------ */

/**
 * Le bandeau d'indicateurs, sous l'en-tête d'un écran d'exploitation.
 *
 * Il était noir, pour ressembler à un tableau de bord d'instruments. C'était
 * la seule surface sombre du produit, et elle tombait au milieu de pages
 * claires: un exploitant y lisait un changement de contexte qui n'existait
 * pas. Il reprend maintenant le dégradé du bandeau d'accueil — même grammaire
 * sur tous les écrans, et les chiffres se lisent sur le fond auquel l'œil
 * s'est habitué partout ailleurs.
 */
const InstrumentBand = ({ title, children, aside, columns, className }) => (
  <RiseIn
    className={cn(
      'relative overflow-hidden rounded-xl border border-border/70 p-4 md:p-5',
      'bg-gradient-to-br from-primary/[0.07] via-card to-flame/[0.06]',
      className,
    )}
  >
    {(title || aside) && (
      <div className="relative mb-4 flex items-center justify-between gap-4">
        {title && (
          <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
            {title}
          </p>
        )}
        {aside}
      </div>
    )}
    {/* Six tiles across only once there is genuinely room: below xl the
        labels wrap onto two lines and the band stops reading as one
        instrument. */}
    <Stagger
      className={cn(
        'relative grid gap-3',
        columns ?? 'grid-cols-2 md:grid-cols-3 xl:grid-cols-4',
      )}
    >
      {React.Children.map(children, (child) => (
        <RiseIn>{child}</RiseIn>
      ))}
    </Stagger>
  </RiseIn>
);
InstrumentBand.displayName = 'InstrumentBand';

/** Pilotage body: decisions get the wide column, supporting charts the narrow. */
const InstrumentGrid = ({ children, className }) => (
  <div className={cn('grid gap-4 xl:grid-cols-[1.4fr_1fr]', className)}>
    {children}
  </div>
);
InstrumentGrid.displayName = 'InstrumentGrid';

/* ------------------------------------------------------------------ */
/* Registry                                                            */
/* ------------------------------------------------------------------ */

/**
 * Filter bar for list screens. Sticks under the header so filters stay
 * reachable through a 200-row scroll — on a registry the filters are the
 * primary control, not decoration at the top of the document.
 */
const RegistryToolbar = ({ children, className }) => (
  <div
    className={cn(
      'sticky top-0 z-20 -mx-4 border-b border-border bg-background/85 px-4 py-3 backdrop-blur',
      'md:-mx-6 md:px-6 lg:-mx-8 lg:px-8',
      'flex flex-wrap items-center gap-2',
      className,
    )}
  >
    {children}
  </div>
);
RegistryToolbar.displayName = 'RegistryToolbar';

/** Compact metric strip above a registry — smaller than a pilotage band. */
const RegistryStrip = ({ children, className }) => (
  <Stagger className={cn('grid grid-cols-2 gap-3 lg:grid-cols-4', className)}>
    {React.Children.map(children, (child) => (
      <RiseIn>{child}</RiseIn>
    ))}
  </Stagger>
);
RegistryStrip.displayName = 'RegistryStrip';

/* ------------------------------------------------------------------ */
/* Record                                                              */
/* ------------------------------------------------------------------ */

/**
 * A record page splits into what the thing IS (left, wide) and what you can
 * DO about it (right, narrow and sticky). The right column stays in view
 * while the timeline scrolls, so an action is never more than a glance away.
 *
 * Deux réglages facultatifs, pour les fiches dont la colonne de droite porte
 * plusieurs blocs (le détail d'un convoi: état, frais, observations):
 *
 *  - `asideFixe` (vrai par défaut) : à partir de xl, la colonne reste sous la
 *    barre du haut et sa hauteur est bornée à l'écran visible. Si elle
 *    déborde, elle défile à l'intérieur d'elle-même une fois collée — voir
 *    `ColonneFixe`. `asideFixe={false}` rend la colonne collante mais sans
 *    défilement interne (comportement historique).
 *  - `asideSuite` : la fin de la colonne de droite. À partir de xl elle fait
 *    partie de la colonne; en dessous, tout s'empile et elle passe APRÈS la
 *    colonne principale (état, puis trajets, puis frais), au lieu de
 *    s'intercaler avant elle.
 */
const RecordSplit = ({ main, aside, asideSuite, asideFixe = true, className }) => {
  const aUneSuite = asideSuite !== undefined && asideSuite !== null && asideSuite !== false;
  const contenu = aUneSuite ? (
    <>
      <div className="min-w-0 space-y-4 max-xl:order-first">{aside}</div>
      <div className="min-w-0 space-y-4 max-xl:order-last">{asideSuite}</div>
    </>
  ) : (
    aside
  );
  // Sous xl, la colonne se dissout dans la grille: ses deux blocs deviennent
  // des éléments de la grille et se rangent par `order`.
  const disposition = aUneSuite ? 'max-xl:contents xl:space-y-4' : 'space-y-4';
  return (
    <div className={cn('grid grid-cols-1 gap-4 xl:grid-cols-[1.5fr_360px]', className)}>
      <Stagger className="min-w-0 space-y-4">
        {React.Children.map(main, (child) => (
          <RiseIn>{child}</RiseIn>
        ))}
      </Stagger>
      {asideFixe ? (
        <ColonneFixe as="aside" point="xl" className={disposition}>
          {contenu}
        </ColonneFixe>
      ) : (
        <aside className={cn(disposition, 'xl:sticky xl:top-20 xl:self-start')}>{contenu}</aside>
      )}
    </div>
  );
};
RecordSplit.displayName = 'RecordSplit';

/* ------------------------------------------------------------------ */
/* Form                                                                */
/* ------------------------------------------------------------------ */

/**
 * A numbered fieldset. The numbering is not decoration: creating a trip is
 * an ordered procedure, and the step number is information the person filling
 * it in actually uses.
 */
const FormStep = ({ step, title, description, children, className }) => (
  <RiseIn
    className={cn('grid gap-4 md:grid-cols-[auto_1fr] md:gap-6', className)}
  >
    <div className="flex items-start gap-3 md:flex-col md:items-center">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border bg-card font-mono text-xs font-bold text-muted-foreground">
        {step}
      </span>
      <span className="hidden w-px flex-1 bg-border md:block" aria-hidden="true" />
    </div>
    <div className="min-w-0 space-y-3 pb-2">
      <div className="flex items-center gap-1">
        <h2 className="text-base">{title}</h2>
        {description && (
          <Aide titre={typeof title === 'string' ? title : 'Cette étape'}>
            {description}
          </Aide>
        )}
      </div>
      {children}
    </div>
  </RiseIn>
);
FormStep.displayName = 'FormStep';

/** Commit bar pinned to the bottom of a form. */
const FormActions = ({ children, hint, className }) => (
  <div
    className={cn(
      'sticky bottom-0 -mx-4 flex flex-wrap items-center justify-between gap-3',
      'border-t border-border bg-background/90 px-4 py-3 backdrop-blur',
      'md:-mx-6 md:px-6 lg:-mx-8 lg:px-8',
      className,
    )}
  >
    {hint ? (
      <p className="text-xs text-muted-foreground">{hint}</p>
    ) : (
      <span />
    )}
    <div className="flex items-center gap-2">{children}</div>
  </div>
);
FormActions.displayName = 'FormActions';

/* ------------------------------------------------------------------ */
/* Map                                                                 */
/* ------------------------------------------------------------------ */

/**
 * Spatial screens give the canvas the room and float everything else over
 * it. A map squeezed into a card is not a map.
 */
const MapCanvas = ({
  children,
  panel,
  height = 'h-[calc(100vh-14rem)]',
  className,
  panelClassName,
}) => (
  <div
    className={cn(
      'relative overflow-hidden rounded-xl border border-border',
      height,
      className,
    )}
  >
    {children}
    {/* The panel is a column, not a scroll box: whatever it holds owns its own
        scrolling, so a list can keep its filter bar fixed while the rows move.
        z-[400] clears Leaflet's own panes, which stop at 200. */}
    {panel && (
      <div
        className={cn(
          'absolute inset-y-3 right-3 z-[400] flex w-80 max-w-[calc(100%-1.5rem)] flex-col overflow-hidden',
          'rounded-lg border border-border bg-card/95 shadow-lg backdrop-blur',
          panelClassName,
        )}
      >
        {panel}
      </div>
    )}
  </div>
);
MapCanvas.displayName = 'MapCanvas';

/* ------------------------------------------------------------------ */
/* Console                                                             */
/* ------------------------------------------------------------------ */

/**
 * System surfaces read as an instrument: dark, monospaced, dense. It should
 * be obvious you have left the business screens and are looking at the
 * machine.
 */
const ConsolePanel = ({ title, actions, children, className }) => (
  <RiseIn
    className={cn(
      'readout-panel overflow-hidden rounded-xl font-mono text-xs',
      className,
    )}
  >
    {(title || actions) && (
      <div className="flex items-center justify-between gap-3 border-b border-readout-grid px-4 py-2.5">
        <p className="uppercase tracking-[0.14em] text-readout-foreground/60">
          {title}
        </p>
        {actions}
      </div>
    )}
    <div className="max-h-[420px] overflow-auto p-4 text-readout-foreground/90">
      {children}
    </div>
  </RiseIn>
);
ConsolePanel.displayName = 'ConsolePanel';

export {
  SplitFixe,
  ColonneFixe,
  useColonneCollee,
  InstrumentBand,
  InstrumentGrid,
  RegistryToolbar,
  RegistryStrip,
  RecordSplit,
  FormStep,
  FormActions,
  MapCanvas,
  ConsolePanel,
};
